import { ProvidedMarket, MarketType, ProvidedMatchCount } from '@/types/menu-setting';
import { useAppearanceStore } from '@/store/appearance';
import { getMarketPriority } from '@/services/settings.service';
import { 
	useCurrentProperties, 
	useMenuDataByMode, 
	generateRoute, 
	getCorrespondingKeyValuePair, 
	MarketData 
} from '../query/use-menu-data';

const hasMatchesInAnyMarket = (sportMarketData: MarketData, availableMarkets: ProvidedMarket[]): boolean => {
	return availableMarkets.some(market => (sportMarketData[market] ?? 0) > 0);
};

/**
 * Find sport market data:
 * 1. First try to find exact match with preferred sport ID that has available matches
 * 2. If not found, return the first sport that has available matches
 */
const findSportMarketData = (menuSports: Array<MarketData>, preferredSport: number, availableMarkets: Array<ProvidedMarket>) => {
	const exactMarketData = menuSports.find(sportMarketData => 
		sportMarketData.GameId === preferredSport && 
		hasMatchesInAnyMarket(sportMarketData, availableMarkets)
	);

	if (exactMarketData) {
		return exactMarketData;
	}

	return menuSports.find(sportMarketData => hasMatchesInAnyMarket(sportMarketData, availableMarkets));
};

export const useSportHref = () => {
	const { sport: preferredSport, market, leagueGroupId } = useCurrentProperties();
	const menuDataByMode = useMenuDataByMode({});
	const view = useAppearanceStore(state => state.view);
	const marketPriority = getMarketPriority(view);
	const markets = Object.keys(marketPriority) as MarketType[];
	const providedMarkets = markets.map(market => marketPriority[market]) as (keyof ProvidedMatchCount)[];;

	const createSportHref = (fallbackRoute: string) => {
		const preferredMarket: ProvidedMarket = market === 'outright' ? 'O' : market === 'u' ? 'WO' : market.toUpperCase() as ProvidedMarket;
		const fallbackToLive = /^\/live/.test(fallbackRoute);
		const fallbackToSports = /^\/sports/.test(fallbackRoute);
		const [eventMode, eventName] = getCorrespondingKeyValuePair((fallbackToLive || fallbackToSports) ? 'sports' : fallbackRoute.replace(/^\/world\/([a-z0-9\-]+)\/\d+\/\w+/, '$1'));
		const menuSports: MarketData[] = menuDataByMode[eventMode] ?? [];
		const availableMarkets: ProvidedMarket[] = fallbackToLive ? ['L'] : providedMarkets;

		const marketDataOfSport = findSportMarketData(menuSports, preferredSport, availableMarkets);
		
		if (eventMode !== 'M0' || !marketDataOfSport) {
			return fallbackRoute;
		}
		const destinationMarket = (availableMarkets.includes(preferredMarket) && marketDataOfSport[preferredMarket]) ? preferredMarket : availableMarkets.find(market => marketDataOfSport[market] ?? 0 > 0) ?? providedMarkets[0]
		const hrefToSport = generateRoute(
			fallbackToLive ? 'live' : fallbackToSports ? 'sports' : `world/${eventName}`,
			marketDataOfSport?.GameId,
			destinationMarket === 'WO' ? 'U' : destinationMarket
		);
		if (!leagueGroupId || !marketDataOfSport.Sub) {
			return hrefToSport;
		} else {
			let indexOfSubMarketData = -1;
			for (let i = marketDataOfSport.Sub.length - 1; i >= 0; i--) {
				const subMarket = marketDataOfSport.Sub[i];
				const isCandidate = i === 0 || subMarket.LeagueGroupId === leagueGroupId;
				const hasPositiveMatchCount = availableMarkets.some(marketToBeVerified => subMarket[marketToBeVerified] ?? 0 > 0);
	
				if (isCandidate && hasPositiveMatchCount) {
					indexOfSubMarketData = i;
					break;
				}
			}
	
			if (indexOfSubMarketData === -1) {
				return hrefToSport;
			}
			const subMarketData = marketDataOfSport.Sub[indexOfSubMarketData];
			const _market = (availableMarkets.includes(preferredMarket) && subMarketData[preferredMarket]) ? preferredMarket : availableMarkets.find(m => subMarketData[m] ?? 0 > 0) || availableMarkets[0];
			return indexOfSubMarketData === -1 ? hrefToSport
				: generateRoute(
					fallbackToLive ? 'live' : fallbackToSports ? 'sports' : `world/${eventName}`,
					subMarketData.GameId,
					_market === 'WO' ? 'U' : _market
				)
				+ (!subMarketData.LeagueGroupId ? '' : `?lgid=${subMarketData.LeagueGroupId}`)
				+ (!subMarketData.GameType ? '' : `?gametype=${subMarketData.GameType}`)
		}
	};

	return {
		createSportHref
	};
};